import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Lock, CheckCircle, CreditCard, Users, Building2, BadgePercent, AlertCircle, Banknote, GraduationCap, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { courseRequiresPecb, PECB_SIGNUP_URL } from "../lib/pecb";
import { upcomingCohorts, formatCohortRange } from "../lib/cohorts";
import { CohortSelector } from "../components/CohortSelector";
import { ProformaRequestDialog } from "../components/ProformaRequestDialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const formatParam = searchParams.get("format") || "self-study";
  const participantsParam = parseInt(searchParams.get("participants")) || 1;
  const isCorporateParam = searchParams.get("corporate") === "true";

  const [course, setCourse] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState(participantsParam);
  const [priceCalculation, setPriceCalculation] = useState(null);

  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Promo
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: isCorporateParam ? "" : null,
  });
  const [errors, setErrors] = useState({});

  // PECB
  const [pecbAck, setPecbAck] = useState(false);
  const [pecbId, setPecbId] = useState("");
  const [participantsList, setParticipantsList] = useState([]);
  const [selectedCohortId, setSelectedCohortId] = useState(searchParams.get("cohort"));

  useEffect(() => {
    setParticipantsList((prev) => {
      const target = Math.max(0, participants - 1);
      const next = [...prev];
      while (next.length < target) next.push({ name: "", email: "", pecb_student_id: "" });
      while (next.length > target) next.pop();
      return next;
    });
  }, [participants]);

  const updateParticipant = (idx, field, value) => {
    setParticipantsList((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/courses/${courseId}`);
        setCourse(res.data);
        const fmt = res.data.delivery_formats?.find((f) => f.format === formatParam) || res.data.delivery_formats?.[0];
        setSelectedFormat(fmt);
      } catch {
        toast.error("Course not found");
        navigate("/courses");
      } finally {
        setLoading(false);
      }
    })();
  }, [courseId, formatParam, navigate]);

  useEffect(() => {
    (async () => {
      if (!course || !selectedFormat) return;
      try {
        const res = await axios.post(`${API}/corporate/calculate-price`, null, {
          params: { course_id: courseId, delivery_format: selectedFormat.format, participants },
        });
        setPriceCalculation(res.data);
      } catch (err) {
        console.error("Price calc error", err);
      }
    })();
  }, [course, selectedFormat, participants, courseId]);

  const requiresPecb = courseRequiresPecb(course);
  const cohorts = upcomingCohorts(course, selectedFormat?.format);
  const selectedCohort = cohorts.find((c) => c.id === selectedCohortId) || null;

  const validateForm = () => {
    const e = {};
    if (cohorts.length > 0 && !selectedCohort) e.cohort = "Please select your course dates";
    if (!formData.name.trim()) e.name = "Name is required";
    if (!formData.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = "Invalid email";
    if (participants > 1 && !formData.company_name?.trim()) e.company_name = "Company name required";
    if (requiresPecb && !pecbAck) e.pecb_ack = "Please acknowledge the PECB Student Account requirement";
    if (requiresPecb && participants > 1) {
      participantsList.forEach((p, i) => {
        if (!p.name?.trim()) e[`p${i}_name`] = "Name required";
        if (!p.email?.trim()) e[`p${i}_email`] = "Email required";
        else if (!/\S+@\S+\.\S+/.test(p.email)) e[`p${i}_email`] = "Invalid email";
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleInput = (ev) => {
    const { name, value } = ev.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: null }));
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError(null);
    const subtotal = priceCalculation?.total_price || (selectedFormat?.price * participants);
    try {
      const res = await axios.post(`${API}/promo/validate`, {
        code: promoCode,
        course_id: courseId,
        delivery_format: selectedFormat.format,
        amount: subtotal,
        customer_email: formData.email || null,
      });
      setPromoDiscount(res.data);
      toast.success(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || "Invalid promo code";
      setPromoError(msg);
      setPromoDiscount(null);
      toast.error(msg);
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setPromoCode("");
    setPromoDiscount(null);
    setPromoError(null);
  };

  const getFinalAmount = () => {
    const base = priceCalculation?.total_price || (selectedFormat?.price * participants);
    if (promoDiscount) return promoDiscount.final_amount;
    return base || 0;
  };

  const customerPayload = () => ({
    course_id: courseId,
    delivery_format: selectedFormat.format,
    customer_name: formData.name,
    customer_email: formData.email,
    customer_phone: formData.phone || null,
    company_name: formData.company_name || null,
    participants,
    participant_emails: participantsList.length ? participantsList.map((p) => p.email).filter(Boolean) : null,
    participants_details: participantsList.length ? participantsList : null,
    pecb_acknowledged: requiresPecb ? pecbAck : false,
    pecb_student_id: requiresPecb && pecbId.trim() ? pecbId.trim() : null,
    cohort_id: selectedCohort?.id || null,
    promo_code: promoDiscount?.code || null,
    promo_discount: promoDiscount?.discount_amount || 0,
  });

  const handleBankTransfer = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/payments/bank-transfer`, customerPayload());
      toast.success("Order created — check your email for bank details");
      navigate(`/orders/${res.data.order_id}/bank-transfer?token=${res.data.token}`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to create order";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStripeCheckout = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/payments/create-stripe-session`, {
        ...customerPayload(),
        origin_url: window.location.origin,
      });
      if (!res.data?.url) throw new Error("No checkout URL returned from server");
      window.location.href = res.data.url;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Unable to start checkout";
      setError(msg);
      toast.error(msg);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <div className="animate-pulse h-96 skeleton rounded-xl" />
        </div>
      </div>
    );
  }

  const isCorporate = participants > 1 || formData.company_name;
  const finalAmount = getFinalAmount();

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
        <div className="mb-8">
          <Link to={`/courses/${courseId}`} className="inline-flex items-center text-muted-foreground hover:text-primary mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Course
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-bold text-3xl text-primary" data-testid="checkout-title">Checkout</h1>
            {isCorporate && (
              <Badge className="bg-purple-100 text-purple-800">
                <Building2 className="h-3 w-3 mr-1" /> Corporate
              </Badge>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Lock className="h-5 w-5 text-accent" /> Enrollment Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {cohorts.length > 0 && (
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <CohortSelector
                      cohorts={cohorts}
                      value={selectedCohort?.id}
                      onChange={(id) => { setSelectedCohortId(id); if (errors.cohort) setErrors((p) => ({ ...p, cohort: null })); }}
                      error={errors.cohort}
                    />
                  </div>
                )}

                {/* Participants */}
                <div className="bg-slate-50 p-4 rounded-lg space-y-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-brand" />
                    <Label className="text-base font-semibold">Number of Participants</Label>
                  </div>
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={participants}
                      onChange={(e) => setParticipants(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-24"
                      data-testid="input-participants"
                    />
                    {priceCalculation?.discount_percent > 0 && (
                      <Badge className="bg-green-100 text-green-800">
                        <BadgePercent className="h-3 w-3 mr-1" /> {priceCalculation.discount_percent}% Group Discount
                      </Badge>
                    )}
                  </div>
                </div>

                {participants > 1 && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-dashed border-brand/40 rounded-lg p-4" data-testid="checkout-proforma-box">
                    <p className="text-sm text-muted-foreground">Need company approval first? Get a verifiable pro forma invoice for this order.</p>
                    <ProformaRequestDialog courseId={courseId} format={selectedFormat?.format} participants={participants} cohortId={selectedCohort?.id} />
                  </div>
                )}

                {participants > 1 && (
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input id="company_name" name="company_name"
                      value={formData.company_name || ""}
                      onChange={handleInput}
                      placeholder="Your company name"
                      className={errors.company_name ? "border-destructive" : ""}
                      data-testid="customer-company-input" />
                    {errors.company_name && <p className="text-sm text-destructive">{errors.company_name}</p>}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name">{participants > 1 ? "Primary Contact Name" : "Full Name"} *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleInput}
                    placeholder="Enter your full name"
                    className={errors.name ? "border-destructive" : ""}
                    data-testid="customer-name-input" />
                  {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleInput}
                    placeholder="your.email@example.com"
                    className={errors.email ? "border-destructive" : ""}
                    data-testid="customer-email-input" />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  <p className="text-xs text-muted-foreground">Course access information will be sent to this email</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number (Optional)</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInput}
                    placeholder="+1 (876) 123-4567" data-testid="customer-phone-input" />
                </div>

                {/* PECB Student Account section */}
                {requiresPecb && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4" data-testid="pecb-section">
                    <div className="flex items-start gap-3">
                      <GraduationCap className="h-5 w-5 text-blue-700 mt-0.5" />
                      <div className="flex-1">
                        <Label className="text-base font-semibold text-blue-900">PECB Student Account</Label>
                        <p className="text-sm text-blue-800 mt-1">
                          To complete this course, each participant needs a free PECB Student Account to access materials and take the certification exam.{" "}
                          <a href={PECB_SIGNUP_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline inline-flex items-center gap-0.5">
                            Create account <ExternalLink className="h-3 w-3" />
                          </a>
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="pecb_id">PECB Student ID {participants > 1 ? "(Primary contact)" : ""} <span className="text-xs text-muted-foreground">(optional)</span></Label>
                      <Input id="pecb_id" value={pecbId} onChange={(e) => setPecbId(e.target.value)} placeholder="Enter your PECB Student ID if you already have one" data-testid="pecb-student-id-input" />
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox checked={pecbAck} onCheckedChange={(v) => { setPecbAck(!!v); if (errors.pecb_ack) setErrors((p) => ({ ...p, pecb_ack: null })); }} className="mt-0.5" data-testid="pecb-ack-checkbox" />
                      <span className="text-sm text-blue-900">
                        I understand that completion of this course requires a free PECB Student Account, and I (or each participant) will create one if not already done.
                      </span>
                    </label>
                    {errors.pecb_ack && <p className="text-sm text-destructive flex items-center gap-1"><AlertCircle className="h-4 w-4" />{errors.pecb_ack}</p>}
                  </div>
                )}

                {requiresPecb && participants > 1 && (
                  <div className="border rounded-lg p-4 space-y-4" data-testid="participants-section">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-brand" />
                      <Label className="text-base font-semibold">Other Participants ({participantsList.length})</Label>
                    </div>
                    <p className="text-xs text-muted-foreground">Enter each additional participant's name, email, and (optionally) PECB Student ID.</p>
                    <div className="space-y-4">
                      {participantsList.map((p, i) => (
                        <div key={i} className="grid md:grid-cols-3 gap-3 p-3 bg-slate-50 rounded-md" data-testid={`participant-row-${i}`}>
                          <div className="space-y-1">
                            <Label className="text-xs">Full Name *</Label>
                            <Input
                              value={p.name}
                              onChange={(e) => updateParticipant(i, "name", e.target.value)}
                              className={errors[`p${i}_name`] ? "border-destructive" : ""}
                              data-testid={`participant-name-${i}`}
                            />
                            {errors[`p${i}_name`] && <p className="text-[11px] text-destructive">{errors[`p${i}_name`]}</p>}
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Email *</Label>
                            <Input
                              type="email"
                              value={p.email}
                              onChange={(e) => updateParticipant(i, "email", e.target.value)}
                              className={errors[`p${i}_email`] ? "border-destructive" : ""}
                              data-testid={`participant-email-${i}`}
                            />
                            {errors[`p${i}_email`] && <p className="text-[11px] text-destructive">{errors[`p${i}_email`]}</p>}
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">PECB Student ID <span className="text-muted-foreground">(optional)</span></Label>
                            <Input
                              value={p.pecb_student_id}
                              onChange={(e) => updateParticipant(i, "pecb_student_id", e.target.value)}
                              data-testid={`participant-pecb-${i}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Promo */}
                <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <BadgePercent className="h-5 w-5 text-brand" />
                    <Label className="text-base font-semibold">Promo Code</Label>
                  </div>
                  {promoDiscount ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="font-semibold text-green-800">{promoDiscount.code}</p>
                          <p className="text-sm text-green-700">{promoDiscount.message}</p>
                        </div>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={removePromo}>Remove</Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input value={promoCode}
                        onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(null); }}
                        placeholder="Enter code"
                        className={`flex-1 uppercase ${promoError ? "border-destructive" : ""}`}
                        data-testid="input-promo-code" />
                      <Button type="button" variant="outline" onClick={handleApplyPromo}
                        disabled={promoLoading || !promoCode.trim()}
                        data-testid="apply-promo-btn">
                        {promoLoading ? "Checking..." : "Apply"}
                      </Button>
                    </div>
                  )}
                  {promoError && (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" /> {promoError}
                    </p>
                  )}
                </div>

                {/* Payment method */}
                <div className="border-t pt-6">
                  <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                    <CreditCard className="h-5 w-5" /> Payment Method
                  </h3>

                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3" data-testid="payment-method-group">
                    <label htmlFor="pm-stripe" className={`flex items-start gap-3 border rounded-lg p-4 cursor-pointer ${paymentMethod === "stripe" ? "border-brand ring-2 ring-brand/20 bg-brand/5" : "border-border"}`}>
                      <RadioGroupItem value="stripe" id="pm-stripe" data-testid="payment-method-stripe-radio" className="mt-1" />
                      <div className="flex-1">
                        <p className="font-semibold flex items-center gap-2"><CreditCard className="h-4 w-4" /> Pay by Credit or Debit Card</p>
                        <p className="text-sm text-muted-foreground">Secure card payment powered by Stripe. Your course access will be activated immediately after successful payment.</p>
                      </div>
                    </label>
                    <label htmlFor="pm-bank" className={`flex items-start gap-3 border rounded-lg p-4 cursor-pointer ${paymentMethod === "bank_transfer" ? "border-brand ring-2 ring-brand/20 bg-brand/5" : "border-border"}`}>
                      <RadioGroupItem value="bank_transfer" id="pm-bank" data-testid="payment-method-bank-transfer-radio" className="mt-1" />
                      <div className="flex-1">
                        <p className="font-semibold flex items-center gap-2"><Banknote className="h-4 w-4" /> Pay by Bank Transfer (USD or JMD)</p>
                        <p className="text-sm text-muted-foreground">Transfer payment to our JMMB Bank Jamaica (USD/JMD) or Wells Fargo United States (USD — ACH or wire) account. Your course access will be activated once payment has been received and verified.</p>
                      </div>
                    </label>
                  </RadioGroup>

                  <div className="mt-6">
                    {paymentMethod === "stripe" ? (
                      <Button
                        type="button"
                        onClick={handleStripeCheckout}
                        disabled={submitting || !finalAmount || (requiresPecb && !pecbAck)}
                        className="w-full bg-brand hover:bg-brand-dark text-white text-lg py-6"
                        data-testid="stripe-checkout-btn"
                      >
                        {submitting ? "Redirecting to secure checkout..." : `Pay Securely with Card — $${finalAmount.toFixed(2)} USD`}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={handleBankTransfer}
                        disabled={submitting || !finalAmount}
                        className="w-full bg-brand hover:bg-brand-dark text-white text-lg py-6"
                        data-testid="bank-transfer-submit-btn"
                      >
                        {submitting ? "Creating order..." : `Continue with Bank Transfer — $${finalAmount.toFixed(2)} USD`}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground pt-2">
                  <div className="flex items-center gap-1"><Lock className="h-4 w-4" /><span>Secure Payment</span></div>
                  <div className="flex items-center gap-1"><CheckCircle className="h-4 w-4" /><span>SSL Encrypted</span></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <Card className="sticky top-24" data-testid="order-summary">
              <CardHeader>
                <CardTitle className="font-heading">Enrollment Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Selected Course:</Label>
                  <p className="font-medium">{course?.title}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Format:</Label>
                  <p className="font-medium">
                    {selectedFormat?.format === "self-study" && "Self-Study"}
                    {selectedFormat?.format === "online-live" && "Online Live"}
                    {selectedFormat?.format === "e-learning" && "E-Learning"}
                  </p>
                </div>
                {selectedCohort && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Course Dates:</Label>
                    <p className="font-medium" data-testid="summary-cohort-dates">{formatCohortRange(selectedCohort)}</p>
                    {selectedCohort.trainer_name && (
                      <p className="text-xs text-muted-foreground">Trainer: {selectedCohort.trainer_name}</p>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Participants:</Label>
                  <p className="font-medium">{participants}</p>
                </div>
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Price per person</span>
                    <span>${priceCalculation?.base_price_per_person || selectedFormat?.price}</span>
                  </div>
                  {participants > 1 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal ({participants} × ${priceCalculation?.base_price_per_person || selectedFormat?.price})</span>
                      <span>${(priceCalculation?.subtotal || (selectedFormat?.price * participants)).toFixed(2)}</span>
                    </div>
                  )}
                  {priceCalculation?.discount_percent > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Group Discount ({priceCalculation.discount_percent}%)</span>
                      <span>-${priceCalculation.discount_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  {promoDiscount && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Promo ({promoDiscount.code})</span>
                      <span>-${promoDiscount.discount_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-heading font-bold text-lg pt-2 border-t">
                    <span>Total</span>
                    <span className="text-brand">${finalAmount.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground text-right">All prices in USD</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
